import React, {memo} from 'react';
import {ButtonGroup as ButtonGroupComponent} from '@pepsico-ds/ui';
import {SIZE} from '@/utils/constants/globalStyle';
import PropTypes from 'prop-types';

/**
 * ButtonGroup component renders a group of buttons based on the provided uiSchema.
 *
 * @param {Object} props - The properties object.
 * @param {Object} props.uiSchema - The schema containing configuration for the button group.
 * @param {Array} props.uiSchema.items - The items to be rendered as buttons.
 * @param {string} props.uiSchema.size - The size of the buttons.
 * @param {string} props.uiSchema.className - Additional class names for styling the button group.
 * @param {boolean} [props.uiSchema.isDark=true] - Flag to determine if the button group should use dark theme.
 * @param {string} props.id - The unique identifier for the button group.
 *
 * @returns {JSX.Element} The rendered ButtonGroup component.
 */
const ButtonGroup = ({uiSchema, id}) => {
  const {items, size = SIZE.MEDIUM, className, isDark = true} = uiSchema;
  return (
    <ButtonGroupComponent
      id={`button-group` + (id ? `_${id}` : '')}
      data-testid={`button-group` + (id ? `_${id}` : '')}
      isDark={isDark}
      items={items}
      size={size}
      className={className}
    />
  );
};

ButtonGroup.propTypes = {
  uiSchema: PropTypes.shape({
    items: PropTypes.array.isRequired,
    size: PropTypes.string,
    className: PropTypes.string,
    isDark: PropTypes.bool
  }).isRequired,
  id: PropTypes.string.isRequired
};

export default memo(ButtonGroup);
